﻿import { lazy, Suspense, useEffect } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import * as Sentry from "@sentry/react";
import { App as AntdApp } from "antd";
import ErrorBoundary from "./components/ErrorBoundary";
import ReniLoader from "./components/ReniLoader";
import PWAUpdateBanner from "./components/PWAUpdateBanner";
import { AntdStaticHelper } from "./utils/antdStatic";
import useActivityTracker from "./hooks/traking/useActivityTracker.jsx";
import { setNavigate } from "./utils/navigationRef";
import TerminosGuard from "./components/terminos/TerminosGuard.jsx";
import ProtectedRoutesAfil from "./guard/Afiliado/ProtectedRouteAfil.jsx";
import ProtectedRoutePres from "./guard/Prestador/ProtectedRoutePres.jsx";
import ProtectedRouteProm from "./guard/Promotor/ProtectedRouteProm.jsx";
import DashboardAfilTemp from "./pages/portal_afiliado/dashboard/DashboardAfilTemp";
import DashboardPrestTemp from "./pages/portal_prestador/dashboard/DashboardPrestTemp";
import DashboardPromTemp from "./pages/protal_promotor/dashboard/DashboardPromTemp";

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

if (import.meta.env.PROD) {
  console.log = () => {};
  console.error = () => {};
  console.warn = () => {};
}

// Reintenta el import una vez antes de propagar el error (stale-chunk recovery)
const lazyWithRetry = (fn) =>
  lazy(() => fn().catch(() => fn()));

// --- Lazy page imports ---
const Login                         = lazyWithRetry(() => import("./pages/login/Login"));
const Register                      = lazyWithRetry(() => import("./pages/register/Register"));
const RessetPassword                = lazyWithRetry(() => import("./pages/resset_password/RessetPassword"));
const PoliticsOfPrivacy             = lazyWithRetry(() => import("./pages/politicsofprivacy/PoliticsOfPrivacy.jsx"));
const NotFound                      = lazyWithRetry(() => import("./pages/notFound/NotFound.jsx"));

// Portal afiliado
const DashboardAfil                 = lazyWithRetry(() => import("./pages/portal_afiliado/dashboard/DashboardAfil"));
const NucleoFamiliar                = lazyWithRetry(() => import("./pages/portal_afiliado/NucleoFamiliar"));
const DisponibilidadMedia           = lazyWithRetry(() => import("./pages/portal_afiliado/DisponibilidadMedia"));
const CuentasAfiliados              = lazyWithRetry(() => import("./pages/portal_afiliado/CuentasAfiliados"));
const PerfilUsuario                 = lazyWithRetry(() => import("./pages/portal_afiliado/PerfilUsuario.jsx"));
const CarnetDigital                 = lazyWithRetry(() => import("./pages/portal_afiliado/CarnetDigital.jsx"));
const Precertificaciones            = lazyWithRetry(() => import("./pages/portal_afiliado/Precertificaciones.jsx"));
const Reembolso                     = lazyWithRetry(() => import("./pages/portal_afiliado/Reembolso.jsx"));
const InclusionDependiente          = lazyWithRetry(() => import("./pages/portal_afiliado/InclusionDependiente.jsx"));
const CoberturaMedicamento          = lazyWithRetry(() => import("./pages/portal_afiliado/CoberturaMedicamento.jsx"));
const CoberturaProcedimiento        = lazyWithRetry(() => import("./pages/portal_afiliado/CoberturaProcedimiento.jsx"));
const HistoricoAutorizaciones       = lazyWithRetry(() => import("./pages/portal_afiliado/HistoricoAutorizaciones.jsx"));
const PresentarQuejas               = lazyWithRetry(() => import("./pages/portal_afiliado/PresentarQuejas.jsx"));
const GastoBolsillo                 = lazyWithRetry(() => import("./pages/portal_afiliado/GastoBolsillo.jsx"));
const ExclusionDependiente          = lazyWithRetry(() => import("./pages/portal_afiliado/ExclusionDependiente.jsx"));
const EstadoDeCuentas               = lazyWithRetry(() => import("./pages/portal_afiliado/EstadoDeCuentas.jsx"));
const SolicitarCobroRecurrente      = lazyWithRetry(() => import("./pages/portal_afiliado/SolicitarCobroRecurrente.jsx"));
const CuentasBancariasAfil          = lazyWithRetry(() => import("./pages/portal_afiliado/CuentasBancariasAfil.jsx"));

// Portal prestador
const DashboardPrest                = lazyWithRetry(() => import("./pages/portal_prestador/dashboard/DashboardPrest.js"));
const ConsultarPagos                = lazyWithRetry(() => import("./pages/portal_prestador/ConsultarPagos.jsx"));
const CuentasPorPagar               = lazyWithRetry(() => import("./pages/portal_prestador/CuentasPorPagar.jsx"));
const TrackAutorizaciones           = lazyWithRetry(() => import("./pages/portal_prestador/TrackAutorizaciones.jsx"));
const EstadoDeCuenta                = lazyWithRetry(() => import("./pages/portal_prestador/EstadoDeCuenta.jsx"));
const CartaRetencion                = lazyWithRetry(() => import("./pages/portal_prestador/CartaRetencion.jsx"));
const MisAutorizaciones             = lazyWithRetry(() => import("./pages/portal_prestador/MisAutorizaciones.jsx"));
const PdfEstadoCuenta               = lazyWithRetry(() => import("./pages/portal_prestador/PdfEstadoCuenta.jsx"));
const PdfCartaRetencion             = lazyWithRetry(() => import("./pages/portal_prestador/PdfCartaRetencion.jsx"));
const PagosUltimoAnio               = lazyWithRetry(() => import("./pages/portal_prestador/PagosUltimoAnio.jsx"));
const AnularAutorizacion            = lazyWithRetry(() => import("./pages/portal_prestador/AnularAutorizacion.jsx"));
const AutorizacionesAnuladas        = lazyWithRetry(() => import("./pages/portal_prestador/AutorizacionesAnuladas.jsx"));
const EstadoNCF                     = lazyWithRetry(() => import("./pages/portal_prestador/EstadoNCF.jsx"));
const CuentasBancarias              = lazyWithRetry(() => import("./pages/portal_prestador/CuentasBancarias.jsx"));
const DetalleDePagos                = lazyWithRetry(() => import("./pages/portal_prestador/DetalleDePagos.jsx"));
const SolicitudPagoPorTransferencia = lazyWithRetry(() => import("./pages/portal_prestador/SolicitudPagoPorTransferencia.jsx"));
const DetalleDePagosPDF             = lazyWithRetry(() => import("./pages/portal_prestador/DetalleDePagosPDF.jsx"));
const MisTarifas                    = lazyWithRetry(() => import("./pages/portal_prestador/MisTarifas.jsx"));

// Portal promotor
const DashboardProm                 = lazyWithRetry(() => import("./pages/protal_promotor/dashboard/DashboardProm.js"));
const ConsultarProduccion           = lazyWithRetry(() => import("./pages/protal_promotor/ConsultarProduccion.jsx"));
const ReporteComisiones             = lazyWithRetry(() => import("./pages/protal_promotor/ReporteComisiones.jsx"));
const ProduccionDiaria              = lazyWithRetry(() => import("./pages/protal_promotor/ProduccionDiaria.jsx"));
const EstadoNuct                    = lazyWithRetry(() => import("./pages/protal_promotor/EstadoNuct.jsx"));
const Viajes                        = lazyWithRetry(() => import("./pages/protal_promotor/Viajes.jsx"));
const RegistroViajes                = lazyWithRetry(() => import("./pages/protal_promotor/RegistroViajes.jsx"));
const ReportePlanViajes             = lazyWithRetry(() => import("./pages/protal_promotor/ReportePlanViajes.jsx"));
const ReporteGeneralViajes          = lazyWithRetry(() => import("./pages/protal_promotor/ReporteGeneralViajes.jsx"));
const EditarViajeNoAutorizado       = lazyWithRetry(() => import("./pages/protal_promotor/EditarViajeNoAutorizado.jsx"));
const PagosDeComisiones             = lazyWithRetry(() => import("./pages/protal_promotor/PagosDeComisiones.jsx"));
const PagosDeComisionesPDF          = lazyWithRetry(() => import("./pages/protal_promotor/PagosDeComisionesPDF.jsx"));

function App() {
  const { usuario, codigoAfiliado, idrol, token } = useSelector((state) => state.user);
  const location = useLocation();
  const navigate  = useNavigate();
  const { logActivity } = useActivityTracker();

  // Registra el navigate de React Router para que axiosClient lo use
  // en errores 401, evitando el window.location.href que recarga la página.
  useEffect(() => { setNavigate(navigate); }, [navigate]);

  const isAuthenticated = codigoAfiliado !== "" && usuario !== "" && idrol !== "";

  useEffect(() => {
    if (isAuthenticated) {
      logActivity(usuario, idrol, location.pathname);
    }
  }, [isAuthenticated, usuario, idrol, location.pathname, logActivity]);

  return (
    <AntdApp>
      <AntdStaticHelper />
      <ErrorBoundary>
      <Suspense fallback={<ReniLoader text="Cargando..." />}>
        <SentryRoutes>
          <Route path="/" element={<Login />} />
          <Route path="/registro" element={<Register />} />
          <Route path="/ressetpasswd" element={<RessetPassword />} />
          <Route path="/politicsofprivacy" element={<PoliticsOfPrivacy />} />

          <Route element={<ProtectedRoutesAfil rol={idrol} token={token} />}>
            <Route element={<DashboardAfilTemp />}>
              <Route path="/dashboardafil" element={<TerminosGuard><DashboardAfil /></TerminosGuard>} />
              <Route path="/nucleofamiliar" element={<NucleoFamiliar />} />
              <Route path="/medicinadisp" element={<DisponibilidadMedia />} />
              <Route path="/cuentasafil" element={<CuentasAfiliados />} />
              <Route path="/miperfil" element={<PerfilUsuario />} />
              <Route path="/carnetdigital" element={<CarnetDigital />} />
              <Route path="/inclusiondepend" element={<InclusionDependiente />} />
              <Route path="/exclusiondepend" element={<ExclusionDependiente />} />
              <Route path="/coberturamedica" element={<CoberturaMedicamento />} />
              <Route path="/coberturaproced" element={<CoberturaProcedimiento />} />
              <Route path="/precertificaciones" element={<Precertificaciones />} />
              <Route path="/reembolso" element={<Reembolso />} />
              <Route path="/Reembolso" element={<Reembolso />} />
              <Route path="/historicoaut" element={<HistoricoAutorizaciones />} />
              <Route path="/quejas" element={<PresentarQuejas />} />
              <Route path="/preciofarmacos" element={<GastoBolsillo />} />
              <Route path="/estadodecuentas" element={<EstadoDeCuentas />} />
              <Route path="/cuentasbancariasafil" element={<CuentasBancariasAfil />} />
              <Route path="/solicitarcobrorecurrente" element={<SolicitarCobroRecurrente />} />
            </Route>
          </Route>

          <Route element={<ProtectedRoutePres rol={idrol} token={token} />}>
            <Route element={<DashboardPrestTemp />}>
              <Route path="/dashboardpres" element={<TerminosGuard><DashboardPrest /></TerminosGuard>} />
              <Route path="/consultarpagos" element={<ConsultarPagos />} />
              <Route path="/estadoncf" element={<EstadoNCF />} />
              <Route path="/cuentasbancarias" element={<CuentasBancarias />} />
              <Route path="/detalledepagos" element={<DetalleDePagos />} />
              <Route path="/solicitudpagoportransferencia" element={<SolicitudPagoPorTransferencia />} />
              <Route path="/cuentasporcobrar" element={<CuentasPorPagar />} />
              <Route path="/pagoultimoanio" element={<PagosUltimoAnio />} />
              <Route path="/trackautorizaciones" element={<TrackAutorizaciones />} />
              <Route path="/cartaretencion" element={<CartaRetencion />} />
              <Route path="/estadocuenta" element={<EstadoDeCuenta />} />
              <Route path="/misautorizaciones" element={<MisAutorizaciones />} />
              <Route path="/anularautorizacion" element={<AnularAutorizacion />} />
              <Route path="/authanuladas" element={<AutorizacionesAnuladas />} />
              <Route path="/mistarifas" element={<MisTarifas />} />
            </Route>
            {/* Vistas PDF sin sidebar */}
            <Route path="/pdfcartaretencion" element={<PdfCartaRetencion />} />
            <Route path="/pdfestadocuenta" element={<PdfEstadoCuenta />} />
            <Route path="/detalledepagospdf" element={<DetalleDePagosPDF />} />
          </Route>

          <Route element={<ProtectedRouteProm rol={idrol} token={token} />}>
            <Route element={<DashboardPromTemp />}>
              <Route path="/dashboardprom" element={<TerminosGuard><DashboardProm /></TerminosGuard>} />
              <Route path="/consultarprod" element={<ConsultarProduccion />} />
              <Route path="/viajes" element={<Viajes />} />
              <Route path="/registrarviajes" element={<RegistroViajes />} />
              <Route path="/reporteplanviajes" element={<ReportePlanViajes />} />
              <Route path="/reportegeneralviajes" element={<ReporteGeneralViajes />} />
              <Route path="/reportecom" element={<ReporteComisiones />} />
              <Route path="/prodicciondia" element={<ProduccionDiaria />} />
              <Route path="/estadonuct" element={<EstadoNuct />} />
              <Route path="/editarviaje" element={<EditarViajeNoAutorizado />} />
              <Route path="/pagocomisiones" element={<PagosDeComisiones />} />
            </Route>
            {/* Vista PDF sin sidebar */}
            <Route path="/pagodecomisionespdf" element={<PagosDeComisionesPDF />} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </SentryRoutes>
      </Suspense>
      <PWAUpdateBanner />
      </ErrorBoundary>
    </AntdApp>
  );
}

export default Sentry.withProfiler(App);
